import React, { useState, useEffect } from 'react'
import { List, Dropdown, Checkbox } from 'antd'

import { NameObj, NameSplitter } from 'pages/contacts/utils/NameSplitter'
import PerfectScrollbar from 'libs/react-perfect-scrollbar'
import { EmptyData } from 'utils/ui'

import classNames from 'classnames'

import { getInitialAvatarSrc } from 'utils/ui'
import unselectAllIcon from 'assets/images/button-unselect-all.svg'

import styles from './style.module.scss'
import i18n from 'i18n'
import { confirmDelete } from 'utils/ui'
import * as contactService from 'pages/contacts/modules/contactService'
import { validateEmail } from 'pages/contacts/utils'

interface ListItemProps {
  name?: string
  email?: string
  phone?: string
  item: any
  pickerMode?: boolean
  checkBoxElem: React.ReactNode
  isEditing?: boolean
}

function ListItem(props: ListItemProps) {
  const { name, email, phone, item, pickerMode, checkBoxElem, isEditing } = props
  const { hasPhoto } = item.contact

  const [photo, setPhoto] = useState<string | null>(null)

  useEffect(() => {
    const fetchPhoto = async () => {
      if (hasPhoto) {
        const p = await contactService.getPhoto(item.serverId)
        setPhoto(p.photo)
      }
    }
    fetchPhoto()
  }, [hasPhoto, item.etag])

  const displayName = name || email || phone || i18n.t('contact.menu.noName')
  const calcName = name || phone || i18n.t('contact.menu.noName')

  const getPhotoUrl = (photo: string | null) => {
    if (!photo) return null
    if (photo.startsWith('data:image')) return photo
    return `data:image/png;base64,${photo}`
  }

  const [imgSrc, setImgSrc] = useState(getInitialAvatarSrc(name, email, { emptyName: calcName }))

  useEffect(() => {
    const p = getPhotoUrl(photo)
    if (p != null) setImgSrc(p)
  }, [photo])

  return (
    <React.Fragment>
      <div
        className={classNames(
          pickerMode ? styles.pickerIcon : styles.icon,
          pickerMode ? '' : styles.hoverHidden,
          isEditing ? styles.avatarHidden : '',
        )}
      >
        <img
          className={pickerMode ? styles.pickerAvatar : styles.avatar}
          src={imgSrc}
          alt="avatar"
          onError={() => {
            setImgSrc(getInitialAvatarSrc(name, email, { emptyName: calcName }))
          }}
        />
        {pickerMode !== true && checkBoxElem}
      </div>
      <div className={styles.info}>
        <div className={styles.nameContainer}>
          <div className={styles.name}>{displayName}</div>
        </div>
        {pickerMode && (
          <div className={styles.emailContainer}>
            <div className={styles.email}>{email}</div>
          </div>
        )}
      </div>
      {pickerMode === true && checkBoxElem}
    </React.Fragment>
  )
}

interface ContactListProps {
  innerRef?: React.MutableRefObject<any>
  initSearchText?: string
  pickerMode?: boolean
  type?: string
  key?: string
  onSelectedChange?: (contact: any) => void
  onSelectChanged?: (emailList: string[], userList: any[]) => void
  excludeEmailList?: string[]
  defaultEmailList?: string[]
  disableEmails?: string[]
  onDuplicate?: (item: any, type: string) => void
  onMoveToShared?: (item: any) => void
  onExport?: (item: any) => void
  size?: string
}

interface ContactListState {
  viewContactList: any[]
  selectedKey: string | null
  checkedKeys: string[]
}

class ContactList extends React.Component<ContactListProps, ContactListState> {
  state: ContactListState = {
    viewContactList: [],
    selectedKey: null,
    checkedKeys: [],
  }

  selectedKey: string | null = null
  checkedKeys: string[] = []

  contactList: any[] = []

  contactMap: any = {}
  contactEmailMap: any = {}

  scrollRef = React.createRef<any>()

  isListFull = false

  offset = 0

  limit = 0

  searchText?: string
  searchTimer?: any

  createCheckedKey = (serverId: string, index: number) => {
    return serverId + '\t' + index.toString()
  }

  convertViewContactList = (contacts: any[]) => {
    let contactList: any = null
    if (this.props.pickerMode) {
      const excludeEmailList =
        this.props.excludeEmailList?.map((email) => email.toLowerCase()) || []
      contactList = []
      contacts.forEach((contact) => {
        contact.contact.emails?.forEach((email: any, index: number) => {
          if (validateEmail(email.email) && excludeEmailList.indexOf(email.email) === -1) {
            contactList.push({
              ...contact,
              index,
              checkedKey: this.createCheckedKey(contact.serverId, index),
            })
          }
        })
      })

      this.props.defaultEmailList?.forEach((email) => {
        const contact = contactList.find((item: any) => item.email === email)
        if (contact) {
          this.checkedKeys.push(contact.checkedKey)
        }
      })
    } else {
      contactList =
        contacts.map((contact) => {
          return {
            ...contact,
            checkedKey: this.createCheckedKey(contact.serverId, 0),
          }
        }) || []
    }
    return contactList
  }

  listener = async () => {
    let contacts: any[] = []
    if (this.searchText != null && this.searchText.length > 0) {
      contacts = await contactService.searchContact(this.searchText)
    } else {
      contacts = contactService.getContacts()
    }

    this.setState({
      viewContactList: contacts,
    })

    if (this.state.selectedKey != null) {
      setTimeout(() => {
        const focus = this.state.viewContactList.find(
          (item) => item.serverId === this.state.selectedKey,
        )
        if (focus) {
          this.props.onSelectedChange?.(focus)
        } else {
          this.props.onSelectedChange?.(this.state.viewContactList[0])
        }
      }, 100)
    }
  }

  componentDidMount() {
    const { innerRef, initSearchText, pickerMode } = this.props

    if (innerRef != null) {
      innerRef.current = this.createRefObj()
    }

    this.initList(initSearchText)

    if (pickerMode !== true) {
      setTimeout(() => {
        const focus = this.state.viewContactList[0]
        if (focus) {
          this.setState({
            selectedKey: focus.serverId,
          })
          if (this.props.onSelectedChange != null) {
            this.props.onSelectedChange(focus)
          }
        }
      }, 100)
    }

    contactService.addContactsUpdatedListener(this.listener)
  }

  componentDidUpdate(prevProps: ContactListProps) {
    const { type, key, initSearchText } = this.props

    if (prevProps != null && (prevProps.type !== type || prevProps.key !== key)) {
      this.initList(initSearchText)
    }
  }

  componentWillUnmount() {
    contactService.removeContactsUpdatedListener(this.listener)
  }

  createRefObj = () => {
    const obj = {
      onSearch: (text: string) => {
        return this.onSearch(text)
      },
      onChange: (e: any) => {
        return this.onChange(e)
      },
      getCheckedItems: () => {
        return this.checkedKeys.map((key) => {
          return this.state.viewContactList.find((item) => key.startsWith(item.serverId))
        })
      },
      reload: () => {
        this.initList(this.searchText)
      },
    }

    return obj
  }

  initList = async (searchText?: string) => {
    this.offset = -20
    this.limit = 20
    this.isListFull = false

    this.selectedKey = null

    this.checkedKeys = []
    this.setState({ checkedKeys: [] })

    this.contactList = []
    this.contactMap = {}
    this.contactEmailMap = {}

    this.searchText = searchText

    let contacts: any[] = []
    if (searchText != null && searchText.length > 0) {
      contacts = await contactService.searchContact(searchText)
    } else {
      contacts = contactService.getContacts()
    }
    console.log('contacts - ', contacts)

    const contactList = this.convertViewContactList(contacts)

    this.setState({
      viewContactList: contactList,
    })

    if (contacts.length === 0) {
      if (this.props.onSelectedChange != null) {
        this.props.onSelectedChange(null)
      }
    } else if (this.state.selectedKey != null) {
      setTimeout(() => {
        const focus = contacts.find((item) => item.serverId === this.state.selectedKey)
        if (focus) {
          this.setState({
            selectedKey: focus.serverId,
          })
          this.props.onSelectedChange?.(focus)
        } else {
          this.setState({
            selectedKey: contacts[0].serverId,
          })
          this.props.onSelectedChange?.(contacts[0])
        }
      }, 100)
    }
  }

  onSearch = (value: string) => {
    this.initList(value)
  }

  onChange = (e: any) => {
    console.log('onChange - ', e)
    const { value } = e.target
    if (this.searchTimer != null) {
      clearTimeout(this.searchTimer)
    }

    this.searchTimer = setTimeout(() => {
      this.searchTimer = null
      this.onSearch(value)
    }, 300)
  }

  onItemClick = (id: string, item: any) => {
    const { onSelectedChange, pickerMode } = this.props
    if (pickerMode) {
      return
    }
    console.log('onItemClick - ', id, item)

    this.setState({
      selectedKey: id,
    })

    if (onSelectedChange != null) {
      onSelectedChange(item)
    }
  }

  nameWithPerson(person: any) {
    if (person) {
      const nameObj = new NameObj({
        prefix: person.prefix,
        first_name: person.first_name,
        last_name: person.last_name,
        middle_name: person.middle_name,
        suffix: person.suffix,
      })
      const nameSplitter = new NameSplitter(navigator.language || 'en-US')
      nameSplitter.guessFullNameStyle(nameObj)
      return nameSplitter.joinWithName(nameObj, true, true)
    }
    return ''
  }

  selectedList() {
    const emailList: string[] = []
    const userList: any[] = []

    this.checkedKeys.forEach((key) => {
      const contact = this.state.viewContactList.find((item) => item.checkedKey === key)
      if (contact != null) {
        const email = contact.contact.emails[contact.index].email
        if (email && emailList.includes(email) === false) {
          emailList.push(email)
          userList.push({
            email,
            name: this.nameWithPerson(contact.contact.person),
            id: contact.serverId,
            etag: contact.etag,
          })
        }
      }
    })

    return {
      emailList,
      userList,
    }
  }

  toggleChecked = (id: string) => {
    if (this.checkedKeys.indexOf(id) > -1) {
      this.checkedKeys = this.checkedKeys.filter((item) => item !== id)
    } else {
      this.checkedKeys.push(id)
    }

    this.setState({
      checkedKeys: this.checkedKeys,
    })

    if (this.props.onSelectChanged != null) {
      const { emailList, userList } = this.selectedList()
      this.props.onSelectChanged(emailList, userList)
    }
  }

  toggleCheckedRange = (id: string) => {
    if (this.checkedKeys.includes(id) === true) {
      this.toggleChecked(id)
      return
    }

    const lastItemId = this.checkedKeys[this.checkedKeys.length - 1]
    const lastItemIdx = this.state.viewContactList.findIndex((item) => {
      if (item.checkedKey === lastItemId) {
        return true
      }
      return false
    })
    const newItemIndex = this.state.viewContactList.findIndex((item) => {
      if (item.checkedKey === id) {
        return true
      }
      return false
    })

    let startIndex: number | null = null
    let endIndex: number | null = null
    if (newItemIndex > lastItemIdx) {
      startIndex = lastItemIdx + 1
      endIndex = newItemIndex
    } else if (newItemIndex < lastItemIdx) {
      startIndex = newItemIndex
      endIndex = lastItemIdx - 1
    } else {
      return
    }

    for (let i = startIndex; i <= endIndex; i++) {
      const item = this.state.viewContactList[i]
      const key = item.checkedKey
      if (this.checkedKeys.includes(key) === true) {
        continue
      }
      this.checkedKeys.push(key)
    }

    this.setState({
      checkedKeys: [...this.checkedKeys],
    })

    if (this.props.onSelectChanged != null) {
      const { emailList, userList } = this.selectedList()
      this.props.onSelectChanged(emailList, userList)
    }
  }

  _runDelete = (item: any) => {
    contactService.deleteContact(item.serverId, item.etag).then((res) => {
      console.log('delete - ', res)
    })
  }

  handleDelete = (item: any) => {
    console.log('handleDelete - ', item)

    confirmDelete({
      title: i18n.t('contact.menu.deleteContact'),
      content: i18n.t('contact.menu.deleteContactDesc'),
      okText: i18n.t('common.button.ok'),
      cancelText: i18n.t('common.button.cancel'),
      onOk: () => {
        this._runDelete(item)
      },
    })
  }

  handleUnselectAll = () => {
    this.checkedKeys = []
    this.setState({ checkedKeys: [] })
  }

  renderItem = (item: any) => {
    const { pickerMode } = this.props
    const { serverId, contact, checkedKey } = item

    const clsProps = { selected: this.state.selectedKey === serverId }
    const { person, emails, phones } = contact

    const name = this.nameWithPerson(person)

    let email = null
    let index = 0
    if (pickerMode) {
      index = item.index || 0
    }
    if (emails != null && emails.length > 0) {
      email = emails[index].email
    }

    let phone = null
    if (phones != null && phones.length > 0) {
      phone = phones[0].number
    }

    const contextMenuItems = [
      {
        key: 'duplicate',
        label: i18n.t('signature.menu.duplicate'),
        children: [
          {
            key: 'duplicateToShared',
            label: i18n.t('contact.menu.duplicateToShared'),
            onClick: () => this.props.onDuplicate?.(item, 'public'),
          },
          {
            key: 'duplicateToPersonal',
            label: i18n.t('contact.menu.duplicateToPersonal'),
            onClick: () => this.props.onDuplicate?.(item, 'personal'),
          },
        ],
      },
      {
        key: 'move',
        label: i18n.t('contact.menu.moveToShared'),
        onClick: () => this.props.onMoveToShared?.(item),
      },
      {
        key: 'export',
        label: i18n.t('contact.menu.export'),
        onClick: () => this.props.onExport?.(item),
      },
      { type: 'divider' as const },
      {
        key: 'delete',
        label: <span style={{ color: 'red' }}>{i18n.t('common.button.delete')}</span>,
        onClick: () => this.handleDelete(item),
        disabled: false,
      },
    ]

    const disabled = this.props.disableEmails?.indexOf(email) > -1

    const checkBoxAttrs = {
      checked:
        this.checkedKeys.indexOf(checkedKey) > -1 || this.props.disableEmails?.indexOf(email) > -1,
    }

    const checkBoxElem = (
      <div
        className={classNames(styles.check, pickerMode ? '' : styles.userCheck)}
        onClick={(e) => {
          if (pickerMode !== true) {
            e.stopPropagation()
            e.preventDefault()
            if (e.shiftKey === true) {
              this.toggleCheckedRange(checkedKey)
            } else {
              this.toggleChecked(checkedKey)
            }
          }
        }}
      >
        <Checkbox
          {...checkBoxAttrs}
          disabled={disabled}
          onClick={(e: any) => {
            e.stopPropagation()
            if (e.shiftKey === true) {
              this.toggleCheckedRange(checkedKey)
            } else {
              this.toggleChecked(checkedKey)
            }
          }}
        />
      </div>
    )

    const DropdownElement = Dropdown as any
    return (
      <DropdownElement
        menu={{ items: contextMenuItems }}
        trigger={['contextMenu']}
        destroyOnHide={true}
        overlayClassName="contextMenu"
      >
        <List.Item
          key={checkedKey}
          className={classNames(styles.contactItem, clsProps)}
          onClick={(e: any) => {
            if (disabled) {
              return
            }
            e.stopPropagation()
            if (this.checkedKeys.length > 0 || this.props.pickerMode) {
              if (e.shiftKey) {
                this.toggleCheckedRange(checkedKey)
              } else {
                this.toggleChecked(checkedKey)
              }
            } else {
              this.onItemClick(serverId, item)
            }
          }}
        >
          <ListItem
            name={name}
            email={email}
            phone={phone}
            item={item}
            checkBoxElem={checkBoxElem}
            pickerMode={this.props.pickerMode}
            isEditing={this.props.pickerMode !== true && this.state.checkedKeys.length > 0}
          />
        </List.Item>
      </DropdownElement>
    )
  }

  render() {
    const { viewContactList } = this.state
    const { size, pickerMode } = this.props

    const listAttrs = {
      itemLayout: 'horizontal' as const,
      dataSource: viewContactList,
      renderItem: this.renderItem,
      rowKey: 'serverId',
      locale: {
        emptyText:
          this.searchText?.length && this.searchText.length > 0 ? (
            <EmptyData type="search" mailbox={null as any} />
          ) : (
            <EmptyData type="contacts" mailbox={null as any} />
          ),
      },
    }

    const clsProps = {
      small: size === 'small',
    }

    const checkedCount = this.checkedKeys.length
    const title =
      checkedCount > 0
        ? i18n.t('contact.title.selected', { count: checkedCount })
        : i18n.t('contact.title.personalContacts') +
          (viewContactList.length === 0
            ? ''
            : ' - ' + i18n.t('contact.title.count', { count: viewContactList.length }))

    return (
      <div
        className={classNames(
          styles.contactList,
          'contantList',
          clsProps,
          checkedCount > 0 ? styles.contactListEditMode : '',
        )}
      >
        {pickerMode !== true && (
          <div className={styles.listHeader}>
            {checkedCount > 0 && (
              <div className={styles.unselectAllBtn} onClick={this.handleUnselectAll}>
                <img src={unselectAllIcon} alt="unselect all" />
              </div>
            )}
            <span>{title}</span>
          </div>
        )}
        <PerfectScrollbar className={styles.scrollView} refScroll={this.scrollRef} height="100%">
          <List {...listAttrs} />
        </PerfectScrollbar>
      </div>
    )
  }
}

export default ContactList
